import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  min2CharValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createExperience,
  getAllExperiences,
  getExperienceByID,
  ExperienceImageUpload,
  updateExperience,
} from "./services/experience.services";
import { clearExperienceDetails } from "./experienceSlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import { getFile } from "../../_helper/helper";
import FormRichTextField from "../../components/formFields/FormRichTextField";

const CreateExperience: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  // const location = useLocation();
  // const ExperienceID = location.state?.ExperienceID;
  const ExperienceID = useAppSelector((state) => state.drawer.data);
  const ExperienceDetail = useAppSelector(
    (state) => state.experience.ExperienceDetail,
  );
  const loading = useAppSelector((state) => state.experience.formLoading);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ExperienceDetail]);

  useEffect(() => {
    if (ExperienceID) {
      dispatch(getExperienceByID(ExperienceID));
    }
    return () => {
      dispatch(clearExperienceDetails());
    };
  }, [ExperienceID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values?.Title ? values?.Title : "",
      Description: values?.Description ? values?.Description : "",
      SequenceNo: values?.SequenceNo ? +values?.SequenceNo : null,
      IsActive: values?.IsActive === 1,
    };
    const ImageData = new FormData();
    const Image =
      values?.Image && typeof values?.Image !== "string"
        ? getFile(values?.Image)
        : "";

    try {
      if (ExperienceID) {
        const editPayload = {
          ...formData,
          ID: ExperienceID,
        };

        const response = await dispatch(updateExperience(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values?.Image && typeof values?.Image !== "string") {
            ImageData.append("Image", Image);
            ImageData.append("ID", ExperienceID);
            await dispatch(ExperienceImageUpload(ImageData));
          }

          dispatch(closed());
          dispatch(getAllExperiences());
          dispatch(clearExperienceDetails());
        }
      } else {
        const response = await dispatch(createExperience(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", Image);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(ExperienceImageUpload(ImageData));
          }

          dispatch(closed());
          dispatch(getAllExperiences());
          dispatch(clearExperienceDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={ExperienceDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout
                style={{ marginRight: 10 }}
                gap={{ rows: 10, cols: 10 }}
                cols={[{ width: "50%" }, { width: "50%" }]}
              >
                <GridLayoutItem className="master-drawer-header" colSpan={2}>
                  <Typography.h4 className="m-0">
                    {ExperienceID ? "Update Experience" : "Add Experience"}
                  </Typography.h4>

                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Title"
                    component={FormTextField}
                    validator={min2CharValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Description"
                    label="Description"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="SequenceNo"
                    label="Sequence No"
                    placeholder="0"
                    format="n0"
                    component={FormNumericTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    justifyContent="start"
                    name={`Image`}
                    label="Image"
                    type="file"
                    alternateName="Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={2}
                  style={{
                    marginTop: "20px",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={ExperienceID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateExperience;
